import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, MessageCircle } from 'lucide-react';
import { MaskedLines, Reveal } from '../components/Motion';
import CTASection from '../components/CTASection';
import { CONTACT, SERVICE_CATEGORIES } from '../lib/data';

export default function Services() {
  useEffect(() => {
    document.title = 'Services | Printing, Packaging, Design & Corporate Merchandise — Shiva Prints & Packs';
  }, []);

  return (
    <>
      <section data-testid="services-hero" className="bg-primary text-primary-foreground noise">
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-40 pb-24 md:pb-32">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="overline">
            Our services
          </motion.p>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] max-w-4xl">
            <MaskedLines onLoad lines={['Complete printing, packaging', <>&amp; <span className="text-accent">corporate</span> solutions.</>]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }} className="mt-8 max-w-2xl text-lg text-primary-foreground/70 leading-relaxed">
            Everything your business needs to look and ship its best — produced with reliable quality,
            low MOQs and nationwide delivery.
          </motion.p>
        </div>
      </section>

      {SERVICE_CATEGORIES.map((cat, idx) => {
        const CatIcon = cat.icon;
        return (
          <section
            key={cat.id}
            data-testid={`service-category-${cat.id}`}
            className={idx % 2 === 1 ? 'bg-muted/60' : 'bg-background'}
          >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
              <Reveal>
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center bg-primary text-primary-foreground"><CatIcon className="h-6 w-6" /></span>
                  <p className="overline">{`0${idx + 1} — Category`}</p>
                </div>
                <h2 className="font-display mt-6 text-3xl md:text-5xl font-medium tracking-tight">{cat.title}</h2>
                <p className="mt-4 max-w-2xl text-lg text-muted-foreground">{cat.tagline}</p>
              </Reveal>

              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {cat.items.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <Reveal key={item.name} delay={i * 0.05} className="group bg-card border border-border p-7 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] transition-shadow duration-300">
                      <Icon className="h-7 w-7 text-accent" />
                      <h3 className="font-display mt-6 text-xl font-medium">{item.name}</h3>
                      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}

      {/* Enquiry strip */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        <Reveal className="bg-card border border-border p-8 md:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl md:text-3xl font-medium">Need something not listed here?</h3>
            <p className="mt-2 text-muted-foreground">Tell us your requirement — we&apos;ll find the right solution.</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <a href={CONTACT.tel} data-testid="services-call" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-4 text-sm font-semibold hover:bg-accent transition-colors duration-300">
              <Phone className="h-4 w-4" /> Call Now
            </a>
            <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="services-whatsapp" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-4 text-sm font-semibold hover:brightness-110 transition-all duration-300">
              <MessageCircle className="h-4 w-4" /> WhatsApp Us
            </a>
          </div>
        </Reveal>
      </section>

      <CTASection eyebrow="Request a quote" title="Send us your specs. Get a clear answer." />
    </>
  );
}
